import { Course } from '../../courses/types';

export const SQL_PHASE1_COURSES: Course[] = [
  {
    id: 'sql-phase1-fundamentals',
    title: 'SQL Phase 1: Database Fundamentals',
    description: 'Master the core concepts of relational databases and SQL basics for beginners.',
    level: 'beginner',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic computer literacy'],
    learningOutcomes: [
      'Understand relational database concepts',
      'Write basic SQL SELECT queries',
      'Use WHERE clauses and filtering',
      'Work with basic JOINs',
      'Understand data types and constraints'
    ],
    type: 'sql',
    modules: [
      {
        id: 'sql-p1-m1',
        title: 'Introduction to Databases',
        description: 'Learn what databases are and why they matter in modern applications.',
        content: `# Introduction to Databases - The Foundation of Data Management

## Understanding Data and Information

### What is Data?
Data represents raw facts, figures, and observations about the world around us. In the digital age, data is everywhere:
- Customer transactions in e-commerce
- Social media interactions and posts
- Sensor readings from IoT devices
- Financial market information
- Medical records and research data

### From Data to Information to Knowledge
\`\`\`
Raw Data → Processed Data → Information → Knowledge → Wisdom
\`\`\`

**Example:**
- **Raw Data**: "25, 30, 22, 28, 35"
- **Information**: "Average temperature this week: 28°C"
- **Knowledge**: "It's been warmer than usual for this time of year"

## Database Fundamentals

### What is a Database?
A database is a systematically organized collection of structured information that can be easily accessed, managed, and updated. Think of it as a digital filing cabinet with powerful search and organization capabilities.

### Key Database Components
\`\`\`
Database System Architecture:
┌─────────────────┐
│   Application   │ (Your web app, mobile app)
├─────────────────┤
│   Database API  │ (SQL queries, connections)
├─────────────────┤
│      DBMS       │ (MySQL, PostgreSQL, etc.)
├─────────────────┤
│   File System   │ (Storage on disk)
└─────────────────┘
\`\`\`

### Database Management System (DBMS)
A DBMS is software that provides:
- **Data Storage**: Efficient storage on disk
- **Data Retrieval**: Fast query processing
- **Data Security**: Access controls and permissions
- **Data Integrity**: Ensuring accuracy and consistency
- **Concurrent Access**: Multiple users simultaneously
- **Backup and Recovery**: Data protection

## Types of Database Systems

### 1. Relational Databases (RDBMS)
**Structure**: Data organized in tables with rows and columns
**Best For**: Structured data with clear relationships

**Popular RDBMS:**
- **MySQL**: Open-source, widely used for web applications
- **PostgreSQL**: Advanced open-source with excellent standards compliance
- **SQL Server**: Microsoft's enterprise database solution
- **Oracle**: Enterprise-grade with advanced features
- **SQLite**: Lightweight, embedded database

**Example Table Structure:**
\`\`\`sql
-- Customers table
CREATE TABLE customers (
    customer_id INT PRIMARY KEY,
    first_name VARCHAR(50) NOT NULL,
    last_name VARCHAR(50) NOT NULL,
    email VARCHAR(100) UNIQUE,
    registration_date DATE,
    total_orders INT DEFAULT 0
);

-- Orders table
CREATE TABLE orders (
    order_id INT PRIMARY KEY,
    customer_id INT,
    order_date DATE,
    total_amount DECIMAL(10,2),
    status VARCHAR(20),
    FOREIGN KEY (customer_id) REFERENCES customers(customer_id)
);
\`\`\`

### 2. NoSQL Databases
**Structure**: Flexible schemas, various data models
**Best For**: Unstructured data, rapid scaling, flexible requirements

**Types of NoSQL:**

#### Document Stores (MongoDB, CouchDB)
\`\`\`json
{
  "_id": "customer_123",
  "name": {
    "first": "John",
    "last": "Doe"
  },
  "email": "john.doe@email.com",
  "addresses": [
    {
      "type": "home",
      "street": "123 Main St",
      "city": "Anytown",
      "zipcode": "12345"
    }
  ],
  "orders": [
    {"order_id": "ord_001", "total": 29.99, "date": "2024-01-15"}
  ]
}
\`\`\`

#### Key-Value Stores (Redis, DynamoDB)
\`\`\`
user:123:profile → {"name": "John", "email": "john@email.com"}
user:123:preferences → {"theme": "dark", "language": "en"}
session:abc123 → {"user_id": 123, "expires": "2024-01-15T10:00:00Z"}
\`\`\`

#### Graph Databases (Neo4j, Amazon Neptune)
\`\`\`
(Person:John)-[:FRIEND]->(Person:Jane)
(Person:John)-[:LIKES]->(Movie:Inception)
(Movie:Inception)-[:DIRECTED_BY]->(Person:Christopher_Nolan)
\`\`\`

## Why Use Databases?

### 1. Data Organization and Structure
Without a database, data might be scattered across files:
\`\`\`
customers.txt:
John,Doe,john@email.com,2024-01-01
Jane,Smith,jane@email.com,2024-01-02

orders.txt:
1,john@email.com,2024-01-15,29.99
2,jane@email.com,2024-01-16,45.50
\`\`\`

With a database, data is structured and relationships are clear.

### 2. Data Integrity and Validation
\`\`\`sql
-- Constraints ensure data quality
CREATE TABLE products (
    product_id INT PRIMARY KEY,
    name VARCHAR(100) NOT NULL,          -- Must have a name
    price DECIMAL(10,2) CHECK (price > 0), -- Price must be positive
    category_id INT,
    stock_quantity INT DEFAULT 0,
    FOREIGN KEY (category_id) REFERENCES categories(id)
);
\`\`\`

### 3. Concurrent Access and Locking
Multiple users can access the same data simultaneously:
\`\`\`sql
-- User A updates inventory
BEGIN TRANSACTION;
UPDATE products SET stock_quantity = stock_quantity - 1 
WHERE product_id = 123;

-- User B tries to read the same product
-- Database handles this automatically with locking mechanisms
SELECT stock_quantity FROM products WHERE product_id = 123;
\`\`\`

### 4. Security and Access Control
\`\`\`sql
-- Create different user roles
CREATE ROLE sales_team;
CREATE ROLE managers;

-- Grant specific permissions
GRANT SELECT ON customers TO sales_team;
GRANT SELECT, UPDATE ON products TO sales_team;
GRANT ALL PRIVILEGES ON orders TO managers;

-- Create users and assign roles
CREATE USER 'john_sales' IDENTIFIED BY 'secure_password';
GRANT sales_team TO 'john_sales';
\`\`\`

### 5. Backup and Recovery
\`\`\`bash
# Automated daily backups
mysqldump --all-databases > backup_$(date +%Y%m%d).sql

# Point-in-time recovery
mysqlbinlog --start-datetime="2024-01-15 09:00:00" \\
           --stop-datetime="2024-01-15 10:00:00" \\
           binlog.000001 | mysql
\`\`\`

## Database Design Principles

### Normalization - Eliminating Redundancy
**Before Normalization (Problems: Redundancy, Update Anomalies):**
\`\`\`sql
-- Bad design - information repeated
customer_orders:
order_id | customer_name | customer_email | product_name | price | quantity
1        | John Doe      | john@email.com| Laptop       | 999   | 1
2        | John Doe      | john@email.com| Mouse        | 25    | 2
3        | Jane Smith    | jane@email.com| Laptop       | 999   | 1
\`\`\`

**After Normalization (Clean, No Redundancy):**
\`\`\`sql
-- Good design - separated into logical tables
customers:
customer_id | name      | email
1          | John Doe  | john@email.com
2          | Jane Smith| jane@email.com

products:
product_id | name   | price
1         | Laptop | 999
2         | Mouse  | 25

orders:
order_id | customer_id | product_id | quantity | order_date
1        | 1          | 1          | 1        | 2024-01-15
2        | 1          | 2          | 2        | 2024-01-16
3        | 2          | 1          | 1        | 2024-01-17
\`\`\`

### ACID Properties - Ensuring Reliability

#### Atomicity - All or Nothing
\`\`\`sql
-- Bank transfer example
BEGIN TRANSACTION;

UPDATE accounts SET balance = balance - 100 WHERE account_id = 'A123';
UPDATE accounts SET balance = balance + 100 WHERE account_id = 'B456';

-- If any statement fails, entire transaction is rolled back
COMMIT; -- Only if both updates succeed
\`\`\`

#### Consistency - Valid State Always
\`\`\`sql
-- Check constraints maintain consistency
ALTER TABLE accounts ADD CONSTRAINT positive_balance 
CHECK (balance >= 0);

-- This transaction would be rejected:
UPDATE accounts SET balance = balance - 1000 WHERE account_id = 'A123';
-- Error: Check constraint violation
\`\`\`

#### Isolation - Concurrent Transactions Don't Interfere
\`\`\`sql
-- Transaction 1
BEGIN TRANSACTION;
SELECT balance FROM accounts WHERE account_id = 'A123'; -- Reads 500

-- Transaction 2 (running simultaneously)
BEGIN TRANSACTION;
UPDATE accounts SET balance = balance + 100 WHERE account_id = 'A123';
COMMIT; -- Account now has 600

-- Back to Transaction 1
SELECT balance FROM accounts WHERE account_id = 'A123'; -- Still reads 500
COMMIT; -- Isolation maintained
\`\`\`

#### Durability - Committed Changes Persist
Once a transaction is committed, the changes are permanent even if the system crashes immediately after.

## Real-World Database Applications

### E-commerce Platform
\`\`\`sql
-- Core tables for online store
customers (customer_id, name, email, address)
products (product_id, name, description, price, stock)
categories (category_id, name, parent_category)
orders (order_id, customer_id, order_date, status, total)
order_items (order_id, product_id, quantity, unit_price)
reviews (review_id, customer_id, product_id, rating, comment)
\`\`\`

### Social Media Platform
\`\`\`sql
-- Social network schema
users (user_id, username, email, profile_image)
posts (post_id, user_id, content, created_at, likes_count)
comments (comment_id, post_id, user_id, content, created_at)
friendships (user_id_1, user_id_2, status, created_at)
user_interests (user_id, interest_id)
\`\`\`

### Healthcare System
\`\`\`sql
-- Medical database structure
patients (patient_id, name, date_of_birth, insurance_info)
doctors (doctor_id, name, specialization, license_number)
appointments (appointment_id, patient_id, doctor_id, date_time, status)
medical_records (record_id, patient_id, doctor_id, diagnosis, treatment)
prescriptions (prescription_id, record_id, medication, dosage, instructions)
\`\`\``,
        duration: '1 hour',
        difficulty: 'beginner',
        order: 1,
        estimatedTime: '60 minutes',
        objectives: [
          'Define what a database is',
          'Understand different types of databases',
          'Explain the benefits of using databases',
          'Identify key database concepts'
        ],
        quiz: {
          questions: [
            {
              id: 'db-intro-1',
              question: 'What does DBMS stand for?',
              options: [
                'Database Management System',
                'Data Base Management Software',
                'Database Modeling System',
                'Data Business Management System'
              ],
              correctAnswer: 0,
              explanation: 'DBMS stands for Database Management System - software that manages databases.'
            },
            {
              id: 'db-intro-2',
              question: 'Which of the following is NOT an ACID property?',
              options: ['Atomicity', 'Consistency', 'Isolation', 'Accessibility'],
              correctAnswer: 3,
              explanation: 'The ACID properties are Atomicity, Consistency, Isolation, and Durability.'
            },
            {
              id: 'db-intro-3',
              question: 'What is the main advantage of relational databases?',
              options: [
                'They store data in documents',
                'They use structured tables with relationships',
                'They only work with numbers',
                'They require no planning'
              ],
              correctAnswer: 1,
              explanation: 'Relational databases store data in structured tables with defined relationships between them.'
            }
          ]
        }
      },
      {
        id: 'sql-p1-m2',
        title: 'SQL Basics and SELECT Statements',
        description: 'Learn how to retrieve data from databases using SELECT statements.',
        content: `# SQL Basics and SELECT Statements

## What is SQL?

SQL (Structured Query Language) is a programming language designed for managing and manipulating relational databases.

### SQL Commands Categories:
- **DDL (Data Definition Language)**: CREATE, ALTER, DROP
- **DML (Data Manipulation Language)**: SELECT, INSERT, UPDATE, DELETE
- **DCL (Data Control Language)**: GRANT, REVOKE

## The SELECT Statement

The SELECT statement is used to query data from a database.

### Basic Syntax:
\`\`\`sql
SELECT column1, column2, ...
FROM table_name;
\`\`\`

### Examples:

#### Select All Columns:
\`\`\`sql
SELECT * FROM employees;
\`\`\`

#### Select Specific Columns:
\`\`\`sql
SELECT first_name, last_name, salary
FROM employees;
\`\`\`

#### Using Aliases:
\`\`\`sql
SELECT first_name AS "First Name",
       last_name AS "Last Name"
FROM employees;
\`\`\`

## Data Types

### Common SQL Data Types:
- **INTEGER**: Whole numbers
- **VARCHAR(n)**: Variable character strings
- **DATE**: Date values
- **DECIMAL(p,s)**: Decimal numbers
- **BOOLEAN**: True/false values

## Best Practices

1. Use meaningful column aliases
2. Format SQL for readability
3. Use uppercase for SQL keywords
4. Comment complex queries
5. Avoid SELECT * in production`,
        duration: '2 hours',
        difficulty: 'beginner',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Write basic SELECT statements',
          'Use column aliases effectively',
          'Understand SQL data types',
          'Follow SQL best practices'
        ],
        practice: {
          task: 'Write SELECT statements to retrieve employee information from a sample database',
          estimatedTime: 30
        },
        quiz: {
          questions: [
            {
              id: 'sql-select-1',
              question: 'Which SQL statement is used to retrieve data from a database?',
              options: ['GET', 'FETCH', 'SELECT', 'RETRIEVE'],
              correctAnswer: 2,
              explanation: 'The SELECT statement is used to retrieve data from a database.'
            },
            {
              id: 'sql-select-2',
              question: 'What does the asterisk (*) represent in SELECT * FROM table?',
              options: ['All rows', 'All columns', 'All tables', 'All databases'],
              correctAnswer: 1,
              explanation: 'The asterisk (*) represents all columns in the table.'
            }
          ]
        }
      },
      {
        id: 'sql-p1-m3',
        title: 'Filtering Data with WHERE',
        description: 'Learn to filter and refine query results using WHERE clauses.',
        content: `# Filtering Data with WHERE

## The WHERE Clause

The WHERE clause is used to filter records based on specified conditions.

### Basic Syntax:
\`\`\`sql
SELECT column1, column2, ...
FROM table_name
WHERE condition;
\`\`\`

## Comparison Operators

### Basic Operators:
- **=**: Equal to
- **<>** or **!=**: Not equal to
- **<**: Less than
- **>**: Greater than
- **<=**: Less than or equal to
- **>=**: Greater than or equal to

### Examples:
\`\`\`sql
-- Employees with salary greater than 50000
SELECT * FROM employees
WHERE salary > 50000;

-- Employees in specific department
SELECT * FROM employees
WHERE department = 'IT';
\`\`\`

## Logical Operators

### AND, OR, NOT:
\`\`\`sql
-- Multiple conditions with AND
SELECT * FROM employees
WHERE department = 'IT' AND salary > 60000;

-- Multiple conditions with OR
SELECT * FROM employees
WHERE department = 'IT' OR department = 'HR';

-- Using NOT
SELECT * FROM employees
WHERE NOT department = 'Sales';
\`\`\`

## Pattern Matching with LIKE

### Wildcards:
- **%**: Represents zero or more characters
- **_**: Represents exactly one character

\`\`\`sql
-- Names starting with 'A'
SELECT * FROM employees
WHERE first_name LIKE 'A%';

-- Names with 'an' anywhere
SELECT * FROM employees
WHERE first_name LIKE '%an%';
\`\`\`

## Range and List Operations

### BETWEEN:
\`\`\`sql
SELECT * FROM employees
WHERE salary BETWEEN 40000 AND 80000;
\`\`\`

### IN:
\`\`\`sql
SELECT * FROM employees
WHERE department IN ('IT', 'HR', 'Finance');
\`\`\`

## Handling NULL Values

### IS NULL and IS NOT NULL:
\`\`\`sql
-- Find employees with no phone number
SELECT * FROM employees
WHERE phone IS NULL;

-- Find employees with phone numbers
SELECT * FROM employees
WHERE phone IS NOT NULL;
\`\`\``,
        duration: '2 hours',
        difficulty: 'beginner',
        order: 3,
        estimatedTime: '120 minutes',
        objectives: [
          'Use WHERE clause for filtering',
          'Apply comparison and logical operators',
          'Use pattern matching with LIKE',
          'Handle NULL values properly'
        ],
        practice: {
          task: 'Create filtered queries using various WHERE conditions',
          estimatedTime: 45
        },
        quiz: {
          questions: [
            {
              id: 'where-1',
              question: 'Which operator is used for pattern matching in SQL?',
              options: ['MATCH', 'LIKE', 'PATTERN', 'FIND'],
              correctAnswer: 1,
              explanation: 'The LIKE operator is used for pattern matching in SQL.'
            },
            {
              id: 'where-2',
              question: 'What does the % wildcard represent?',
              options: ['Exactly one character', 'Zero or more characters', 'One or more characters', 'Zero characters only'],
              correctAnswer: 1,
              explanation: 'The % wildcard represents zero or more characters.'
            }
          ]
        }
      }
    ]
  }
];